// Opening and closing the reader as a view transition: the browser snapshots the page,
// applies the change, and animates between the two (styles under `[data-vt]` in app.css).
// Moving from one article to the next is not wrapped: it stays immediate.
import { flushSync } from "react-dom";
import { preserveListAnchor } from "./listAnchor";

type Kind = "open" | "close";
type Doc = Document & { startViewTransition?: (update: () => void) => { finished: Promise<void>; skipTransition(): void } };

let current: (() => void) | undefined;

export function asTransition(kind: Kind, change: () => void, articleId?: string, options: { animate?: boolean } = {}): void {
  current?.();
  const doc = document as Doc;
  const anchor = articleId ? preserveListAnchor(articleId) : undefined;
  const root = document.documentElement;
  let applied = false;
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let transition: { finished: Promise<void>; skipTransition(): void } | undefined;
  const apply = () => {
    if (applied || cancelled) return;
    applied = true;
    flushSync(change);
    anchor?.restore(); // settle the new list before the browser snapshots it
  };
  const finish = () => {
    clearTimeout(timer);
    if (!cancelled) anchor?.restore();
    if (current === cancel) { current = undefined; delete root.dataset.vt; }
  };
  const cancel = () => {
    cancelled = true;
    transition?.skipTransition();
    anchor?.stop();
    finish();
  };
  current = cancel;
  if (options.animate === false || !doc.startViewTransition || matchMedia("(prefers-reduced-motion: reduce)").matches) {
    apply();
    requestAnimationFrame(() => requestAnimationFrame(finish));
    return;
  }
  root.dataset.vt = kind;
  transition = doc.startViewTransition(apply);
  void transition.finished.then(finish, finish);
  // The change waits for the browser's next frame. If frames are held back (a tab that is open but
  // not being painted), never leave the article open or shut late: skip the animation and apply it.
  timer = setTimeout(() => { if (!applied && !cancelled) { transition?.skipTransition(); apply(); } }, 300);
}
